import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'
import { useToast } from './use-toast'
import { useAdmin } from './useAdmin'

export interface Organization {
  id: string
  name: string
  slug: string
  logo_url?: string | null
  role: 'owner' | 'admin' | 'member'
  created_by?: string | null
  created_at: string
  updated_at: string
  enabled_modules: string[]
  status?: string | null
}

export interface OrganizationMember {
  id: string
  invite_id?: string
  user_id: string | null
  org_id: string
  role: 'owner' | 'admin' | 'member'
  created_at: string
  name?: string | null
  email?: string
  avatar_url?: string | null
  type: 'member' | 'invite'
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://oitlhxqyfcxwterungzs.supabase.co";

export function useOrganizations() {
  const { user } = useAuth()
  const { toast } = useToast()
  const { isAdmin: isAdminMaster, loading: adminLoading } = useAdmin()
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<'timeout' | 'error' | null>(null)
  
  // Refs para evitar múltiplas chamadas simultâneas
  const fetchingRef = useRef(false)
  const initializedRef = useRef(false)
  const hasLoadedOnceRef = useRef(false)
  const previousAdminMasterRef = useRef<boolean | undefined>(undefined)
  // Ref da org atual para preservar seleção ao atualizar a lista (não perder ao dar refresh)
  const currentOrgIdRef = useRef<string | null>(null)

  // Captura orgId da URL antes de qualquer efeito (passado pelo app Fluity ao navegar para cá).
  // Valida formato UUID para rejeitar qualquer valor malformado antes de tocar no estado.
  const [urlOrgId] = useState(() => {
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    const v = new URLSearchParams(window.location.search).get('orgId')
    return v && UUID_RE.test(v) ? v : null
  })

  const currentOrgStorageKey = user ? `fluity_current_org_${user.id}` : null
  const getStoredOrgId = () => (currentOrgStorageKey ? localStorage.getItem(currentOrgStorageKey) : null) || null
  const setStoredOrgId = (orgId: string | null) => {
    if (currentOrgStorageKey) {
      if (orgId) localStorage.setItem(currentOrgStorageKey, orgId)
      else localStorage.removeItem(currentOrgStorageKey)
    }
    // Manter chave legada para compatibilidade
    if (orgId) localStorage.setItem('currentOrgId', orgId)
    else localStorage.removeItem('currentOrgId')
  }

  // Cancelar subscription diretamente
  const cancelSubscription = async (orgId: string): Promise<boolean> => {
    if (!user) {
      throw new Error('Usuário não autenticado');
    }

    const { data, error } = await supabase.auth.getSession();
    if (error || !data.session) {
      throw new Error('Sessão inválida');
    }

    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${data.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orgId }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Erro ao cancelar subscription');
      }

      return true;
    } catch (err) {
      console.error('Erro ao cancelar subscription:', err);
      throw err;
    }
  };

  const resendInvite = async (params: { inviteId?: string; orgId: string; email: string }) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Sessão inválida para reenviar convite')
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/send-invite-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          invite_id: params.inviteId,
          org_id: params.orgId,
          invited_email: params.email,
        }),
      })

      const result = await response.json().catch(() => null)
      if (!response.ok || result?.success === false) {
        const message =
          result?.error ||
          result?.message ||
          'Erro ao reenviar e-mail de convite'
        throw new Error(message)
      }

      return true
    } catch (error) {
      console.error('Error resending invite:', error)
      return false
    }
  }

  const ORG_FETCH_TIMEOUT_MS = 20000; // 20s - evita loading infinito se a rede travar

    // Fetch all organizations where user is a member
  const fetchUserOrganizations = useCallback(async () => {
    if (!user) {
      return
    }

    // Evitar múltiplas chamadas simultâneas
    if (fetchingRef.current) {
      return
    }

    try {
      fetchingRef.current = true
      // Só mostrar loading na carga inicial, para não perder a org atual ao atualizar/refresh
      if (!hasLoadedOnceRef.current) setLoading(true)
      
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Timeout ao carregar organizações (rede lenta ou indisponível)')), ORG_FETCH_TIMEOUT_MS)
      );

      const fetchOrgs = async (): Promise<Organization[]> => {
        let orgs: Organization[] = [];

      if (isAdminMaster) {
        // Se for Admin Master, buscar TODAS as organizações da plataforma
        const { data, error } = await supabase
          .from('organizations')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) {
          throw error;
        }

        orgs = (data || []).map(org => ({
          ...org,
          role: 'owner' as const, // Admin Master tem poder de owner em tudo
        }));
      } else {
        // Se for usuário comum, buscar apenas as que ele é membro
        const { data, error } = await supabase
          .from('organization_members')
          .select(`
            org_id,
            role,
            organizations (
              id,
              name,
              slug,
              logo_url,
              created_by,
              created_at,
              updated_at,
              enabled_modules,
              status
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })

        if (error) {
          throw error;
        }

        orgs = (data || [])
          .map(item => {
            if (!item.organizations || typeof item.organizations !== 'object') {
              return null
            }
            
            return {
              id: (item.organizations as any).id,
              name: (item.organizations as any).name,
              slug: (item.organizations as any).slug,
              logo_url: (item.organizations as any).logo_url,
              role: item.role as 'owner' | 'admin' | 'member',
              created_by: (item.organizations as any).created_by,
              created_at: (item.organizations as any).created_at,
              updated_at: (item.organizations as any).updated_at,
              enabled_modules: (item.organizations as any).enabled_modules ?? [],
              status: (item.organizations as any).status ?? null,
            }
          })
          .filter((org): org is Organization => org !== null && !!org.id)
      }
        return orgs;
      };

      const orgs = await Promise.race([fetchOrgs(), timeoutPromise]);

      // Sempre aplicar o resultado do fetch: logo_url, nome, etc. podem mudar sem alterar a lista de IDs.
      setOrganizations(orgs)
      setLoadError(null)
      hasLoadedOnceRef.current = true
      
    } catch (error: any) {
      const isTransient =
        error?.code === 'PGRST002' ||
        (error as any)?.status === 503 ||
        error?.message?.toLowerCase().includes('schema cache') ||
        error?.message?.toLowerCase().includes('service unavailable')

      if (isTransient) {
        console.warn('[useOrganizations] Servidor temporariamente indisponível, tentando novamente em 5s.', error?.code)
        setTimeout(() => {
          fetchingRef.current = false
          fetchUserOrganizations()
        }, 5000)
        return
      } else if (error?.message?.includes('Timeout')) {
        setLoadError('timeout')
        console.warn('Organizações: requisição não respondeu a tempo. Verifique a conexão ou abra a aba Network (F12) antes de recarregar.')
        toast({
          title: "Erro de conexão",
          description: "Tempo limite excedido ao carregar organizações. Saia e entre novamente.",
          variant: "destructive",
        })
      } else {
        setLoadError('error')
        toast({
          title: "Erro",
          description: `Erro ao carregar organizações: ${error.message || 'Erro desconhecido'}`,
          variant: "destructive",
        })
      }
    } finally {
      setLoading(false)
      fetchingRef.current = false
    }
  }, [user, toast, isAdminMaster])

  // Create new organization
  const createOrganization = async (name: string, slug: string) => {
    if (!user) return null

    try {
      // Check if slug already exists and generate unique one if needed
      let uniqueSlug = slug
      let attempts = 0
      const maxAttempts = 10

      while (attempts < maxAttempts) {
        const { data: existing, error: checkError } = await supabase
          .from('organizations')
          .select('id')
          .eq('slug', uniqueSlug)
          .maybeSingle()

        // Se não encontrou (erro PGRST116) ou não há dados, slug está disponível
        if (checkError?.code === 'PGRST116' || !existing) {
          // Slug is available
          break
        }

        // Generate unique suffix using user ID, timestamp and random string
        const timestamp = Date.now().toString(36)
        const randomStr = Math.random().toString(36).substring(2, 6)
        const userPrefix = user.id.substring(0, 6)
        const suffix = `${userPrefix}-${timestamp}-${randomStr}`
        uniqueSlug = `${slug}-${suffix}`
        attempts++
      }

      if (attempts >= maxAttempts) {
        throw new Error('Não foi possível gerar um slug único. Tente novamente.')
      }

      // Create organization with default free plan
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name,
          slug: uniqueSlug,
          created_by: user.id,
          plan_id: 'c5c78c8d-1902-4cc7-990a-716be3d005c5' // Default to free plan
        })
        .select()
        .single()

      if (orgError) {
        // Handle specific error codes
        if (orgError.code === '23505') { // Unique violation
          throw new Error('Já existe uma organização com este nome. Tente um nome diferente.')
        }
        throw orgError
      }

      // Add user as owner
      const { error: memberError } = await supabase
        .from('organization_members')
        .insert({
          user_id: user.id,
          org_id: org.id,
          role: 'owner'
        })

      if (memberError) {
        // If member creation fails, try to delete the organization
        await supabase.from('organizations').delete().eq('id', org.id)
        throw memberError
      }

      // Verificar se é a primeira organização do usuário
      const wasFirstOrg = organizations.length === 0

      // Refresh organizations
      await fetchUserOrganizations()

      // Selecionar a organização recém-criada como atual (evita ficar "nenhuma selecionada")
      currentOrgIdRef.current = org.id
      setStoredOrgId(org.id)

      toast({
        title: "Organização criada",
        description: `A organização "${name}" foi criada com sucesso`,
      })

      // Se era a primeira organização, marcar que precisa escolher produtos
      if (wasFirstOrg) {
        // Disparar evento customizado para notificar que uma organização foi criada
        window.dispatchEvent(new CustomEvent('organization-created', { detail: { orgId: org.id } }))
      }

      return org
    } catch (error: any) {
      console.error('Error creating organization:', error)
      
      let errorMessage = "Erro ao criar organização"
      if (error?.code === '23505') {
        errorMessage = "Já existe uma organização com este nome. Tente um nome diferente."
      } else if (error?.message) {
        errorMessage = error.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }

      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive",
      })
      return null
    }
  }

  // Invite user to organization
  const inviteUser = async (orgId: string, email: string, role: 'admin' | 'member' = 'member') => {
    try {
      // Check if invited email is already a member or has pending invite
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', email)
        .maybeSingle()

      if (existingUser) {
        // Check if user is already a member
        const { data: existingMember } = await supabase
          .from('organization_members')
          .select('id')
          .eq('org_id', orgId)
          .eq('user_id', existingUser.id)
          .maybeSingle()

        if (existingMember) {
          toast({
            title: "Erro",
            description: "Este usuário já é membro desta organização",
            variant: "destructive",
          })
          return false
        }
      }

      // Check if there's already a pending invite
      const { data: existingInvite } = await supabase
        .from('organization_invites')
        .select('id')
        .eq('org_id', orgId)
        .eq('invited_email', email)
        .eq('status', 'pending')
        .maybeSingle()

      if (existingInvite) {
        toast({
          title: "Erro",
          description: "Já existe um convite pendente para este email",
          variant: "destructive",
        })
        return false
      }

      // Get current user
      const { data: { user: currentUser } } = await supabase.auth.getUser()
      if (!currentUser) {
        throw new Error('Usuário não autenticado')
      }

      // Create invite in organization_invites table
      const { data: inviteData, error: inviteError } = await supabase
        .from('organization_invites')
        .insert({
          org_id: orgId,
          invited_email: email,
          invited_by: currentUser.id
        })
        .select()
        .single()

      if (inviteError) throw inviteError

      // Chamar Edge Function para enviar e-mail
      try {
        const { data: { session } } = await supabase.auth.getSession()
        
        if (session?.access_token && inviteData) {
          const response = await fetch(`${SUPABASE_URL}/functions/v1/send-invite-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ invite_id: inviteData.id }),
          })

          const emailResult = await response.json().catch(() => null)
          if (!response.ok || emailResult?.success === false) {
            toast({
              title: "Convite criado, mas o e-mail falhou",
              description: emailResult?.message || emailResult?.error || "Verifique as configurações de envio de e-mail no Supabase.",
              variant: "destructive",
            })
          }
        }
      } catch (emailError) {
        toast({
          title: "Convite criado, mas o e-mail falhou",
          description: "Não foi possível enviar o e-mail de convite.",
          variant: "destructive",
        })
      }

      toast({
        title: "Convite enviado!",
        description: `Um convite foi enviado para ${email}`,
      })

      return true
    } catch (error) {
      console.error('Error inviting user:', error)
      toast({
        title: "Erro",
        description: "Erro ao enviar convite",
        variant: "destructive",
      })
      return false
    }
  }

  // Get organization members and pending invites
  const getOrganizationMembers = async (orgId: string): Promise<OrganizationMember[]> => {
    try {
      // Get current members from organization_members table
      const { data: members, error: membersError } = await supabase
        .from('organization_members')
        .select(`
          id,
          user_id,
          org_id,
          role,
          created_at
        `)
        .eq('org_id', orgId)
        .order('created_at', { ascending: true })

      if (membersError) throw membersError

      // Get pending invites from organization_invites table
      const { data: invites, error: invitesError } = await supabase
        .from('organization_invites')
        .select(`
          id,
          org_id,
          invited_email,
          status,
          created_at
        `)
        .eq('org_id', orgId)
        .eq('status', 'pending')
        .order('created_at', { ascending: true })

      if (invitesError) throw invitesError

      // Format members and get user details
      const membersList = await Promise.all((members || []).map(async (member) => {
        // Get user profile data including email
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('name, email, avatar_url')
          .eq('id', member.user_id)
          .maybeSingle()

        if (profileError) {
          // Silently handle profile fetch errors
        }

        const processedMember = {
          ...member,
          role: member.role as 'owner' | 'admin' | 'member',
          type: 'member' as const,
          name: profile?.name || null,
          email: profile?.email || null,
          avatar_url: profile?.avatar_url || null
        }
        
        return processedMember
      }))

      // Format invites - manter visíveis para gestão (cancelar/reenviar), mesmo se houver e-mail já cadastrado
      const invitesList = (invites || [])
        .map(invite => ({
          id: `invite-${invite.id}`, // Unique ID to avoid conflicts
          invite_id: invite.id,
          user_id: null,
          org_id: invite.org_id,
          role: 'member' as const,
          created_at: invite.created_at,
          name: null,
          email: invite.invited_email,
          avatar_url: null,
          type: 'invite' as const
        }))

      return [...membersList, ...invitesList]
    } catch (error) {
      console.error('Error fetching organization members:', error)
      return []
    }
  }

  // Delete organization (for owners or creators)
  const deleteOrganization = async (orgId: string) => {
    try {
      // Check if user is owner or creator
      const organization = organizations.find(org => org.id === orgId)
      
      if (!organization) {
        toast({
          title: "Erro",
          description: "Organização não encontrada",
          variant: "destructive",
        })
        return false
      }

      // Verificar se é owner OU se é o criador da organização
      const isOwner = organization.role === 'owner'
      const isCreator = organization.created_by === user?.id

      if (!isOwner && !isCreator) {
        toast({
          title: "Erro",
          description: "Apenas o proprietário ou o criador da organização pode deletá-la",
          variant: "destructive",
        })
        return false
      }

      // First, check if organization has an active subscription
      const { data: subscription, error: subError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('org_id', orgId)
        .eq('status', 'active')
        .maybeSingle()

      if (subError && subError.code !== 'PGRST116') { // PGRST116 = not found
        throw new Error('Erro ao verificar subscription da organização')
      }

      // If there's an active subscription, cancel it first
      if (subscription) {
        try {
          await cancelSubscription(orgId)
          toast({
            title: "Subscription cancelada",
            description: "A subscription foi cancelada com sucesso",
          })
        } catch (cancelError) {
          toast({
            title: "Erro",
            description: "Erro ao cancelar subscription. Tente novamente.",
            variant: "destructive",
          })
          return false
        }
      }

      // Now delete organization (cascade will handle related records)
      const { error } = await supabase
        .from('organizations')
        .delete()
        .eq('id', orgId)

      if (error) {
        throw error;
      }

      // If deleted org was current, switch to another or clear
      if (currentOrg?.id === orgId) {
        const remainingOrgs = organizations.filter(org => org.id !== orgId)
        if (remainingOrgs.length > 0) {
          currentOrgIdRef.current = remainingOrgs[0].id
          setCurrentOrg(remainingOrgs[0])
          setStoredOrgId(remainingOrgs[0].id)
        } else {
          currentOrgIdRef.current = null
          setCurrentOrg(null)
          setStoredOrgId(null)
        }
      }

      // Refresh organizations
      await fetchUserOrganizations()

      toast({
        title: "Organização deletada",
        description: "A organização foi deletada com sucesso",
      })

      return true
    } catch (error) {
      console.error('Error deleting organization:', error)
      toast({
        title: "Erro",
        description: "Erro ao deletar organização",
        variant: "destructive",
      })
      return false
    }
  }

  // Remove member from organization
  const removeMember = async (orgId: string, memberId: string) => {
    try {
      // Check if user has permission (owner or admin)
      const organization = organizations.find(org => org.id === orgId)
      if (!organization || (organization.role !== 'owner' && organization.role !== 'admin')) {
        toast({
          title: "Erro",
          description: "Você não tem permissão para remover membros",
          variant: "destructive",
        })
        return false
      }

      // Check if trying to remove owner
      const { data: member } = await supabase
        .from('organization_members')
        .select('role')
        .eq('id', memberId)
        .single()

      if (member?.role === 'owner') {
        toast({
          title: "Erro",
          description: "Não é possível remover o proprietário da organização",
          variant: "destructive",
        })
        return false
      }

      // Remove member
      const { error } = await supabase
        .from('organization_members')
        .delete()
        .eq('id', memberId)

      if (error) throw error

      toast({
        title: "Membro removido",
        description: "O membro foi removido da organização com sucesso",
      })

      return true
    } catch (error) {
      console.error('Error removing member:', error)
      toast({
        title: "Erro",
        description: "Erro ao remover membro",
        variant: "destructive",
      })
      return false
    }
  }

  // Update member role
  const updateMemberRole = async (orgId: string, memberId: string, newRole: 'admin' | 'member') => {
    try {
      // Check if user has permission (owner only)
      const organization = organizations.find(org => org.id === orgId)
      if (!organization || organization.role !== 'owner') {
        toast({
          title: "Erro",
          description: "Apenas o proprietário pode alterar funções",
          variant: "destructive",
        })
        return false
      }

      // Check if trying to change owner role
      const { data: member } = await supabase
        .from('organization_members')
        .select('role')
        .eq('id', memberId)
        .single()

      if (member?.role === 'owner') {
        toast({
          title: "Erro",
          description: "Não é possível alterar a função do proprietário",
          variant: "destructive",
        })
        return false
      }

      // Update member role
      const { error } = await supabase
        .from('organization_members')
        .update({ role: newRole })
        .eq('id', memberId)

      if (error) throw error

      toast({
        title: "Função atualizada",
        description: `A função do membro foi alterada para ${newRole === 'admin' ? 'Administrador' : 'Membro'}`,
      })

      return true
    } catch (error) {
      console.error('Error updating member role:', error)
      toast({
        title: "Erro",
        description: "Erro ao atualizar função do membro",
        variant: "destructive",
      })
      return false
    }
  }

  // Cancel invite
  const cancelInvite = async (orgId: string, inviteId: string) => {
    try {
      // Usar função SECURITY DEFINER para garantir que funcione mesmo com RLS
      const { data, error } = await supabase
        .rpc('cancel_organization_invite', {
          invite_id: inviteId,
          org_uuid: orgId
        })

      if (error) {
        toast({
          title: "Erro",
          description: error.message || "Erro ao cancelar convite",
          variant: "destructive",
        })
        return false
      }

      if (!data) {
        toast({
          title: "Erro",
          description: "Não foi possível cancelar o convite. Verifique se o convite existe e se você tem permissão.",
          variant: "destructive",
        })
        return false
      }
      
      toast({
        title: "Convite cancelado",
        description: "O convite foi cancelado com sucesso",
      })

      return true
    } catch (error) {
      console.error('Error canceling invite:', error)
      toast({
        title: "Erro",
        description: "Erro ao cancelar convite",
        variant: "destructive",
      })
      return false
    }
  }

  // Switch current organization
  const switchOrganization = (org: Organization) => {
    currentOrgIdRef.current = org.id
    setCurrentOrg(org)
    setStoredOrgId(org.id)
  }

  // Aplicar orgId recebido via URL (navegação cross-domain desde o app Fluity).
  // Roda uma única vez na montagem para que currentOrgIdRef seja definido antes dos
  // demais efeitos de seleção de org lerem o ref.
  useEffect(() => {
    if (!urlOrgId) return
    currentOrgIdRef.current = urlOrgId
    // Limpar o parâmetro da URL para evitar re-aplicação em recargas subsequentes
    const url = new URL(window.location.href)
    url.searchParams.delete('orgId')
    window.history.replaceState(null, '', url.toString())
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Persistir no localStorage quando o usuário estiver disponível (chave por usuário)
  useEffect(() => {
    if (!user || !urlOrgId) return
    setStoredOrgId(urlOrgId)
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Restaurar ref da última org ao ter usuário (antes da lista carregar)
  useEffect(() => {
    if (!user) return
    const stored = getStoredOrgId()
    if (stored && !currentOrgIdRef.current) {
      currentOrgIdRef.current = stored
    }
  }, [user?.id])

  // Load current organization from storage e definir primeira org se necessário
  // Prioridade: 1) ref da org atual (se estiver na lista), 2) localStorage (última seleção do usuário), 3) primeira da lista
  useEffect(() => {
    if (!organizations.length || !user) {
      return
    }

    const savedOrgId = getStoredOrgId()
    // Preferir a org que já está selecionada (ref) ou a última salva por este usuário
    const preferredOrgId = currentOrgIdRef.current || savedOrgId
    const preferredOrg = preferredOrgId ? organizations.find(org => org.id === preferredOrgId) : null

    if (preferredOrg) {
      currentOrgIdRef.current = preferredOrg.id
      setStoredOrgId(preferredOrg.id)
      // Sempre espelhar a entrada da lista (ex.: logo_url atualizada no servidor)
      setCurrentOrg(preferredOrg)
      return
    }

    // savedOrgId existia mas não está na lista (ex.: foi removido da org) – não sobrescrever com primeira
    if (savedOrgId) {
      return
    }

    // Sem preferência: usar primeira org e persistir como nova "última seleção"
    const firstOrg = organizations[0]
    currentOrgIdRef.current = firstOrg.id
    setStoredOrgId(firstOrg.id)
    setCurrentOrg(firstOrg)
  }, [organizations, user?.id])

  // Subscription em tempo real para novas organizações (apenas para AdminMaster)
  useEffect(() => {
    if (!isAdminMaster || !user) return

    let channel: any = null

    const setupSubscription = async () => {
      channel = supabase
        .channel('admin-organizations-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'organizations',
          },
          () => {
            // Quando uma nova organização é criada ou atualizada, recarregar a lista
            // Usar setTimeout para evitar múltiplas chamadas simultâneas
            setTimeout(() => {
              fetchUserOrganizations()
            }, 500)
          }
        )
        .subscribe()
    }

    setupSubscription()

    return () => {
      if (channel) {
        supabase.removeChannel(channel)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminMaster, user?.id]) // Usar user?.id para evitar re-subscriptions desnecessárias

  // Carregar organizações assim que o usuário estiver disponível (não bloquear em adminLoading).
  // Admin master: re-fetch quando isAdminMaster virar true (efeito abaixo).
  useEffect(() => {
    if (user && !initializedRef.current && !fetchingRef.current) {
      initializedRef.current = true
      previousAdminMasterRef.current = isAdminMaster
      fetchUserOrganizations()
    } else if (!user) {
      initializedRef.current = false
      hasLoadedOnceRef.current = false
      currentOrgIdRef.current = null
      previousAdminMasterRef.current = undefined
      setOrganizations([])
      setCurrentOrg(null)
      setLoadError(null)
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  // Re-buscar organizações quando isAdminMaster mudar (especialmente de false para true)
  // IMPORTANTE: Usar um debounce/guard para evitar disparos transitórios ao trocar de aba
  useEffect(() => {
    // Só processar se já inicializamos e não estamos buscando
    if (!user || !initializedRef.current || fetchingRef.current) {
      // Se ainda não inicializamos, apenas salvar o valor atual
      if (user && !initializedRef.current) {
        previousAdminMasterRef.current = isAdminMaster
      }
      return
    }

    const previousAdminMaster = previousAdminMasterRef.current
    const currentAdminMaster = isAdminMaster
    
    // Se o valor não mudou, não fazer nada
    if (previousAdminMaster === currentAdminMaster) {
      return
    }
    
    // Só re-buscar se isAdminMaster mudou de false para true (mudança real e estável)
    // Ignorar mudanças de true para false ou undefined para true (transitórias)
    if (previousAdminMaster === false && currentAdminMaster === true) {
      // Adicionar um pequeno delay para garantir que não é uma mudança transitória
      const timeoutId = setTimeout(() => {
        // Verificar novamente se o valor ainda é true (não mudou durante o delay)
        if (isAdminMaster === true && !fetchingRef.current) {
          previousAdminMasterRef.current = true
          fetchUserOrganizations()
        }
      }, 100) // 100ms de debounce para evitar mudanças transitórias
      
      return () => clearTimeout(timeoutId)
    } else {
      // Para outras mudanças, apenas atualizar a referência sem buscar
      previousAdminMasterRef.current = currentAdminMaster
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminMaster, user?.id]) // Monitorar mudanças em isAdminMaster e user

  return {
    organizations,
    currentOrg,
    loading,
    loadError,
    fetchUserOrganizations,
    createOrganization,
    inviteUser,
    getOrganizationMembers,
    deleteOrganization,
    removeMember,
    updateMemberRole,
    cancelInvite,
    resendInvite,
    switchOrganization,
  }
}
